/* eslint-disable no-console */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  AzureCommunicationTokenCredential,
  getIdentifierRawId,
} from "@azure/communication-common";
import {
  CallClient,
  VideoStreamRenderer,
  LocalVideoStream,
  TeamsCallAgent,
  TeamsCall,
  VideoDeviceInfo,
  RemoteParticipant,
  RemoteVideoStream,
  CreateViewOptions,
} from "@azure/communication-calling";

import qs from "query-string";
import { Trans } from "react-i18next";

import { useLocation, useNavigate } from "react-router-dom";

import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";

import CircularProgress from "@mui/material/CircularProgress";
import Title from "../../../components/Title";
import Curve from "../../../components/Animations/PageTransitions/Curve";

import Camera from "../../../assets/icons/Camera.svg";
import CameraOff from "../../../assets/icons/CameraOff.svg";
import Calling from "../../../assets/icons/Calling.svg";
import Microphone from "../../../assets/icons/Microphone.svg";
import MicrophoneOff from "../../../assets/icons/MicrophoneOff.svg";
import Bell from "../../../assets/icons/Bell.svg";
import BackButton from "../../../components/BackButton";

import { useRingTimeout } from "../../../lib/hooks/useRingTimeout";

const Controls = styled("div")({
  margin: "1rem 0 0",
  width: "70%",

  "@media screen and (orientation: landscape)": {
    margin: "0 0 1rem 0",
    width: "37%",
  },
});

const CallControl = styled("div")<{ isEnd?: boolean }>(({ theme, isEnd }) => ({
  display: "flex",
  alignItems: "center",
  height: "7.25rem",
  padding: "1.75rem 4.5rem",
  margin: isEnd ? "0" : "5rem 0 0",
  border: "2px solid white",
  borderRadius: "1rem",
  backgroundColor: isEnd ? "#D13438" : theme.palette.primary.main,

  h3: {
    color: "white",
    marginLeft: "2.75rem",
    fontSize: "2.75rem",
  },

  ":only-of-type": isEnd
    ? {
        marginLeft: "auto",
        marginBottom: "2rem",
      }
    : {},
}));

const StreamControl = styled("div")({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: "1.75rem 0",
  width: "17rem",

  h3: {
    marginTop: "1rem",
    color: "black",
    fontSize: "2.25rem",
    textAlign: "center",
  },
});

const StreamContainer = styled("div")({
  width: "50%",
  height: "100%",
  display: "flex",
  flexDirection: "column",

  "@media screen and (orientation: landscape)": {
    flexDirection: "row",
    width: "50%",
  },
});

const Stream = styled("div")({
  width: "100%",
  height: "50%",
  border: "1px solid gray",
  background: " #f2f2f2",
  borderRadius: "1rem",
  overflow: "hidden",
  margin: "1.5rem 0 0 0",

  "@media screen and (orientation: landscape)": {
    width: "50%",
    height: "100%",
    margin: "0 0 0 1.5rem",
  },
});

const StreamMedia = styled("div")({
  position: "absolute",
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,

  "& > *": {
    width: "100%",
    height: "100%",
  },

  "& video": {
    width: "100%",
    height: "100%",
  },
});

const Initials = styled("div")({
  width: "10rem",
  height: "10rem",
  borderRadius: "50%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: "#D3D3D3",
  color: "black",
  fontSize: "3rem",
  fontWeight: "bold",
});

const VIEW_OPTIONS: CreateViewOptions = { scalingMode: "Crop" };

const getInitials = (displayName?: string) =>
  (displayName || "Unknown")
    .split(" ")
    .map((word) => word[0])
    .join("")
    .toUpperCase();

const TeamsVideoGroupCall = () => {
  const navigate = useNavigate();
  const { search } = useLocation();

  const query = qs.parse(search);

  const callerAcsToken = query.callerAcsToken as string;

  const targetTeamsUserIds = query.targetTeamsUserIds as string;

  const allTargetUserIds = useMemo(
    () => targetTeamsUserIds?.split(",").map((id) => id.trim()) || [],
    [targetTeamsUserIds],
  );

  const [triggerCall, setTriggerCall] = useState<boolean>(false);
  const [callsStarted, setCallsStarted] = useState<boolean>(false);

  const [callClient, setCallClient] = useState<CallClient | null>(null);
  const [teamsCallAgent, setTeamsCallAgent] = useState<TeamsCallAgent | null>(
    null,
  );

  const [activeCalls, setActiveCalls] = useState<TeamsCall[]>([]);
  const [connectedCall, setConnectedCall] = useState<TeamsCall | null>(null);
  const activeCallsRef = useRef<TeamsCall[]>([]);
  const connectedCallRef = useRef<TeamsCall | null>(null);

  useEffect(() => {
    activeCallsRef.current = activeCalls;
  }, [activeCalls]);

  useEffect(() => {
    connectedCallRef.current = connectedCall;
  }, [connectedCall]);

  const localVideoRef = useRef<HTMLDivElement | null>(null);
  const [activeLocalVideoStream, setActiveLocalVideoStream] =
    useState<LocalVideoStream | null>(null);
  const [localVideoRenderer, setLocalVideoRenderer] =
    useState<VideoStreamRenderer | null>(null);

  const remoteVideoRef = useRef<HTMLDivElement | null>(null);

  const remoteVideoRendererRef = useRef<VideoStreamRenderer | null>(null);

  const handledParticipantIds = useRef(new Set<string>());

  const [isMicActive, setIsMicActive] = useState<boolean>(true);

  const [displayRemoteInitials, setDisplayRemoteInitials] =
    useState<boolean>(false);
  const [remoteParticipantInitials, setRemoteParticipantInitials] =
    useState<string>("??");

  const [isRinging, setIsRinging] = useState<boolean>(false);

  const [showNoAnswerMessage, setShowNoAnswerMessage] =
    useState<boolean>(false);

  const clearRemoteStream = useCallback(() => {
    remoteVideoRendererRef.current?.dispose();
    remoteVideoRendererRef.current = null;

    remoteVideoRef.current?.replaceChildren();

    setDisplayRemoteInitials(true);
  }, []);

  const renderRemoteStream = useCallback(async (stream: RemoteVideoStream) => {
    remoteVideoRendererRef.current?.dispose();

    const renderer = new VideoStreamRenderer(stream);

    const view = await renderer.createView(VIEW_OPTIONS);

    remoteVideoRendererRef.current = renderer;
    remoteVideoRef.current?.replaceChildren(view.target);

    setDisplayRemoteInitials(false);
  }, []);

  const handleRemoteVideoStream = useCallback(
    (stream: RemoteVideoStream) => {
      if (stream.mediaStreamType !== "Video") {
        return;
      }

      stream.on("isAvailableChanged", () => {
        if (stream.isAvailable) {
          renderRemoteStream(stream);
          return;
        }

        clearRemoteStream();
      });

      if (stream.isAvailable) {
        renderRemoteStream(stream);
        return;
      }

      setDisplayRemoteInitials(true);
    },
    [renderRemoteStream, clearRemoteStream],
  );

  const handleRemoteParticipant = useCallback(
    (participant?: RemoteParticipant) => {
      if (!participant) {
        return;
      }

      const participantId = getIdentifierRawId(participant.identifier);

      if (handledParticipantIds.current.has(participantId)) {
        return;
      }

      handledParticipantIds.current.add(participantId);

      setRemoteParticipantInitials(getInitials(participant.displayName));

      setDisplayRemoteInitials(true);

      participant.videoStreams.forEach(handleRemoteVideoStream);

      participant.on("videoStreamsUpdated", ({ added, removed }) => {
        added.forEach(handleRemoteVideoStream);

        if (removed.some((stream) => stream.mediaStreamType === "Video")) {
          clearRemoteStream();
        }
      });
    },
    [handleRemoteVideoStream, clearRemoteStream],
  );

  const startLocalVideo = useCallback(async () => {
    if (connectedCall?.state === "Connected" && activeLocalVideoStream) {
      const renderer = new VideoStreamRenderer(activeLocalVideoStream);
      const view = await renderer.createView(VIEW_OPTIONS);

      setLocalVideoRenderer(renderer);
      localVideoRef.current?.replaceChildren(view.target);

      if (connectedCall) {
        await connectedCall.startVideo(activeLocalVideoStream);
      }
    }
  }, [activeLocalVideoStream, connectedCall]);

  const stopLocalVideo = useCallback(async () => {
    if (connectedCall && activeLocalVideoStream) {
      await connectedCall.stopVideo(activeLocalVideoStream);
    }

    if (localVideoRenderer) {
      localVideoRenderer.dispose();
      setLocalVideoRenderer(null);
    }
  }, [localVideoRenderer, connectedCall, activeLocalVideoStream]);

  const startCall = useCallback(async () => {
    const currentTeamsCallAgent = teamsCallAgent;
    const currentTargetUserIds = allTargetUserIds;

    if (!currentTeamsCallAgent || currentTargetUserIds.length === 0) {
      console.log(
        "Cannot start call: Target Teams User Ids are missing or Teams Call Agent not initialized.",
      );
      return;
    }

    try {
      let selectedCamera: VideoDeviceInfo | null = null;
      let previewLocalStream: LocalVideoStream | null = null;

      if (!callClient) {
        console.log("Call client not available for video stream");
        return;
      }

      const deviceManager = await callClient.getDeviceManager();
      const cameras = await deviceManager.getCameras();

      if (!cameras.length) {
        console.log("No camera devices found");
        return;
      }

      const [cameraFromList] = cameras;
      selectedCamera = cameraFromList || null;

      previewLocalStream = new LocalVideoStream(selectedCamera);

      const renderer = new VideoStreamRenderer(previewLocalStream);
      const view = await renderer.createView(VIEW_OPTIONS);
      setLocalVideoRenderer(renderer);
      localVideoRef.current?.replaceChildren(view.target);

      const calls: TeamsCall[] = [];

      await Promise.all(
        currentTargetUserIds.map(async (userId) => {
          console.log("Starting call to user:", userId);

          try {
            let userLocalStream: LocalVideoStream | undefined;

            if (selectedCamera) {
              userLocalStream = new LocalVideoStream(selectedCamera);
            }

            const options = userLocalStream
              ? { videoOptions: { localVideoStreams: [userLocalStream] } }
              : {};

            const call = currentTeamsCallAgent.startCall(
              { microsoftTeamsUserId: userId.trim() },
              options,
            );

            calls.push(call);

            call.on("stateChanged", async () => {
              console.log(`Call ${call.id} to ${userId} state: ${call.state}`);

              if (call.state === "Connected" && !connectedCallRef.current) {
                const participant = call.remoteParticipants[0];
                let accepted = false;

                try {
                  await call.muteIncomingAudio();
                  console.log("Incoming audio muted for voicemail detection");
                } catch (error) {
                  console.log("Failed to mute incoming audio:", error);
                }

                const acceptCall = async () => {
                  if (accepted || connectedCallRef.current) {
                    return;
                  }

                  accepted = true;

                  try {
                    await call.unmuteIncomingAudio();
                  } catch (error) {
                    console.log("Failed to unmute incoming audio:", error);
                  }

                  connectedCallRef.current = call;
                  setConnectedCall(call);
                  setActiveLocalVideoStream(userLocalStream || null);
                  setIsMicActive(!call.isMuted);
                  setIsRinging(false);

                  handleRemoteParticipant(participant);

                  const otherCalls = activeCallsRef.current.filter(
                    (c) => c.id !== call.id && c.state !== "Disconnected",
                  );

                  await Promise.all(
                    otherCalls.map((c) =>
                      c.hangUp().catch((e) => console.log("HangUp failed", e)),
                    ),
                  );
                };

                const handleStreamsUpdated = async () => {
                  if ((participant.videoStreams || []).length === 0) {
                    return;
                  }

                  console.log("Valid stream found, accepting call");

                  participant.off("videoStreamsUpdated", handleStreamsUpdated);

                  await acceptCall();
                };

                participant.on("videoStreamsUpdated", handleStreamsUpdated);

                setTimeout(async () => {
                  if (accepted) {
                    return;
                  }

                  console.log(
                    "Timeout passed with no video stream → keeping the call as audio",
                  );

                  participant.off("videoStreamsUpdated", handleStreamsUpdated);

                  await acceptCall();
                }, 4000);
              }

              if (call.state === "Disconnected") {
                console.log(`Call ${call.id} disconnected`);

                const noAcceptedCalls = !connectedCallRef.current;

                const allDisconnected = activeCallsRef.current.every(
                  (c) => c.state === "Disconnected",
                );

                if (noAcceptedCalls && allDisconnected) {
                  console.log("All calls disconnected and none were accepted.");
                  setShowNoAnswerMessage(true);

                  setTimeout(() => {
                    navigate("/", { replace: true });
                  }, 6000);
                }

                if (connectedCallRef.current?.id === call.id) {
                  navigate("/", { replace: true });
                }
              }

              if (call.state === "Ringing" && !connectedCallRef.current) {
                setIsRinging(true);
              }
            });
          } catch (error) {
            console.log(`Failed to start call to ${userId}:`, error);
          }
        }),
      );

      setActiveCalls(calls);
      console.log(`Started ${calls.length} parallel calls`);
    } catch (error) {
      console.log("Failed to start calls: ", error);
    }
  }, [
    teamsCallAgent,
    callClient,
    allTargetUserIds,
    navigate,
    handleRemoteParticipant,
  ]);

  const handleRingTimeout = useCallback(async () => {
    console.log("No call was answered in time. Hanging up the ringing ones.");

    // The state handler above turns the last disconnect into the "no answer"
    // message and takes the visitor back, so hanging up is all this has to do.
    await Promise.all(
      activeCallsRef.current
        .filter((call) => call.state !== "Disconnected")
        .map((call) =>
          call
            .hangUp()
            .catch((error) =>
              console.log("Failed to hang up unanswered call:", error),
            ),
        ),
    );
  }, []);

  useRingTimeout(isRinging && !connectedCall, handleRingTimeout);

  const hangUpCall = useCallback(async () => {
    if (connectedCall) {
      await connectedCall.hangUp();
    }

    clearRemoteStream();

    navigate("/", { replace: true });
  }, [connectedCall, clearRemoteStream, navigate]);

  const toggleMicrophone = useCallback(async () => {
    if (!connectedCall) {
      return;
    }

    if (isMicActive) {
      connectedCall.mute();
    } else {
      connectedCall.unmute();
    }

    setIsMicActive((prev) => !prev);
  }, [connectedCall, isMicActive]);

  useEffect(() => {
    if (triggerCall && !callsStarted) {
      setCallsStarted(true);
      startCall();
    }
  }, [triggerCall]);

  useEffect(() => {
    const initializeCallAgent = async () => {
      if (!callerAcsToken) {
        navigate("/", { replace: true });
        return;
      }

      try {
        const client = new CallClient();

        const tokenCredential = new AzureCommunicationTokenCredential(
          callerAcsToken.trim(),
        );

        const agent = await client.createTeamsCallAgent(tokenCredential);

        setCallClient(client);
        setTeamsCallAgent(agent);

        const deviceManager = await client.getDeviceManager();

        await deviceManager.askDevicePermission({ video: true, audio: true });

        console.log("Call agent initialized.");

        setTriggerCall(true);
      } catch (error) {
        console.log("Failed to initialize call agent:", error);
      }
    };

    initializeCallAgent();
  }, [callerAcsToken, navigate]);

  useEffect(() => {
    return () => {
      (async () => {
        await Promise.all(
          activeCallsRef.current
            .filter((call) => call.state !== "Disconnected")
            .map(async (call) => {
              try {
                await call.hangUp();
              } catch (error) {
                console.log("Failed to hang up call:", call.id, error);
              }
            }),
        );

        remoteVideoRendererRef.current?.dispose();
        remoteVideoRendererRef.current = null;

        setActiveCalls([]);
        setConnectedCall(null);
        setActiveLocalVideoStream(null);
        setLocalVideoRenderer(null);
      })();
    };
  }, []);

  return (
    <Curve>
      <div className="d-flex align-items-center flex-column h-100">
        <BackButton to="/" />
        <Title className="mt-3 mb-4">
          <Typography variant="h1">
            <Trans i18nKey="teamsCall:title" />
          </Typography>
        </Title>
        {!showNoAnswerMessage ? (
          <>
            <Controls className="d-flex justify-content-between align-items-center">
              {!teamsCallAgent ? (
                <CircularProgress
                  size="8rem"
                  style={{
                    marginTop: "5rem",
                    marginLeft: "auto",
                    marginRight: "auto",
                  }}
                />
              ) : (
                <>
                  <div className="d-flex">
                    <StreamControl
                      onClick={() => {
                        if (!localVideoRenderer) {
                          startLocalVideo();
                          return;
                        }
                        stopLocalVideo();
                      }}
                    >
                      {!!localVideoRenderer ? (
                        <>
                          <Camera width="4rem" fill="black" />
                          <Typography variant="h3">
                            <Trans i18nKey="teamsCall:controls.camera" />
                          </Typography>
                        </>
                      ) : (
                        <>
                          <CameraOff width="4rem" fill="black" />
                          <Typography variant="h3">
                            <Trans i18nKey="teamsCall:controls.cameraOff" />
                          </Typography>
                        </>
                      )}
                    </StreamControl>
                    <StreamControl onClick={toggleMicrophone}>
                      {isMicActive ? (
                        <>
                          <Microphone width="4rem" fill="black" />
                          <Typography variant="h3">
                            <Trans i18nKey="teamsCall:controls.mic" />
                          </Typography>
                        </>
                      ) : (
                        <>
                          <MicrophoneOff width="4rem" fill="black" />
                          <Typography variant="h3">
                            <Trans i18nKey="teamsCall:controls.micOff" />
                          </Typography>
                        </>
                      )}
                    </StreamControl>
                  </div>
                  <CallControl onClick={hangUpCall} isEnd>
                    <Calling
                      width="3.5rem"
                      fill="white"
                      style={{ transform: "rotate(135deg)" }}
                    />
                    <Typography variant="h3">
                      <Trans i18nKey="teamsCall:controls.endCall" />
                    </Typography>
                  </CallControl>
                </>
              )}
            </Controls>
            {teamsCallAgent && (
              <StreamContainer>
                <Stream className="position-relative">
                  {!localVideoRenderer && (
                    <div className="position-absolute h-100 w-100 d-flex justify-content-center align-items-center">
                      <Initials className="initials">DR</Initials>
                    </div>
                  )}

                  <StreamMedia id="localVideo" ref={localVideoRef} />
                </Stream>
                <Stream className="position-relative">
                  {displayRemoteInitials && !isRinging && connectedCall && (
                    <div className="position-absolute h-100 w-100 d-flex justify-content-center align-items-center">
                      <Initials className="initials position-relative">
                        {remoteParticipantInitials}
                      </Initials>
                    </div>
                  )}
                  {isRinging && (
                    <div className="position-absolute h-100 w-100 d-flex flex-column justify-content-center align-items-center">
                      <Bell width="3.5rem" />
                      <Typography variant="h4" className="fs-3 mt-2">
                        <Trans i18nKey="teamsCall:controls.ringing" />
                      </Typography>
                    </div>
                  )}
                  <StreamMedia id="remoteVideo" ref={remoteVideoRef} />
                </Stream>
              </StreamContainer>
            )}
          </>
        ) : (
          <div className="mb-5 pb-5 h-100 d-flex justify-content-center align-items-center w-100">
            <Typography variant="h2" className="text-center text-danger">
              <Trans i18nKey="teamsCall:controls.noAnswer" />
            </Typography>
          </div>
        )}
      </div>
    </Curve>
  );
};

export default TeamsVideoGroupCall;
